import { Bleed, Box, BreakPoints, Flex, SafeArea, Stack, useBreakpoint } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { StickyTopBanner } from '@components/sticky_top_banner/sticky_top_banner.component';
import { useFeatureFlag } from '@services/feature_flag/use_feature_flag';
import { useCurrentLocation } from '@src/application/use_current_location';
import styled, { css } from 'styled-components';
import { useSelectedEntrance } from '../../application/use_selected_entrance';

interface ReceptionHomeLayoutProps {
	actionSection: JSX.Element[];
	heroSection: JSX.Element | null;
	aboutUsSection: JSX.Element | null;
	footerSection: JSX.Element | null;
}

const elements = {
	uniqueButtons: styled.div<{ buttonCount: number }>`
		display: grid;
		grid-column-gap: ${tokens.spacing.x1};
		grid-row-gap: ${tokens.spacing.x1};
		grid-template-columns: repeat(1, 1fr);
		width: 100%;

		${tokens.breakpointQuery.medium} {
			grid-column-gap: ${tokens.spacing.x3};
			grid-row-gap: ${tokens.spacing.x3};

			${({ buttonCount }) => {
				if (buttonCount === 1 || buttonCount % 2 === 0)
					return css`
						grid-template-columns: repeat(2, 1fr);
					`;

				return css`
					grid-template-columns: repeat(3, 1fr);
				`;
			}};
		}
	`,
	backgroundContainer: styled.div`
		flex-grow: 1;
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
	`,
	backgroundSafeArea: styled.div`
		width: 100%;
		max-width: 1096px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x4};
		padding: ${tokens.spacing.x4} ${tokens.spacing.x2};

		${tokens.breakpointQuery.medium} {
			padding: ${tokens.spacing.x6} ${tokens.spacing.x2};
		}
	`,
};

export function ReceptionHomeLayout({
	aboutUsSection,
	heroSection,
	actionSection,
	footerSection,
	...rest
}: ReceptionHomeLayoutProps): JSX.Element {
	const [_, config] = useCurrentLocation();
	const useReceptionBackground = useFeatureFlag('PatientWeb.UseReceptionBackground');

	return (
		<Box background={tokens.color.bg.pageBrand} grow='1' {...rest}>
			<SafeArea maxWidth='1096px'>
				<Banner />
				{heroSection}
			</SafeArea>
			<elements.backgroundContainer
				style={
					useReceptionBackground && config?.theme.BackgroundImageUrl
						? { backgroundImage: `url(${config.theme.BackgroundImageUrl as string})` }
						: {}
				}
			>
				<elements.backgroundSafeArea>
					<elements.uniqueButtons buttonCount={actionSection.length}>{actionSection}</elements.uniqueButtons>
					{aboutUsSection}
					{footerSection}
				</elements.backgroundSafeArea>
			</elements.backgroundContainer>
		</Box>
	);
}

function Banner(): JSX.Element | null {
	const breakpoint = useBreakpoint();
	const [unitNode] = useSelectedEntrance();

	if (breakpoint === BreakPoints.Large) return null;

	return <StickyTopBanner variant='leapScroll'>{unitNode?.name ?? ''}</StickyTopBanner>;
}
